/**
 * Parent story case studies.
 * Only entries with consentVerified === true are rendered, linked, or added to the sitemap.
 *
 * Short parent clips live in public/videos/testimonails/{name}.mp4 (with sound).
 * The homepage bubble uses a muted 480×480 copy in public/videos/bubble/.
 * Written fields (age, service, quote, transcript) stay empty until the clinic sends them.
 */

export type CaseStudyService =
  | "Speech Therapy"
  | "Occupational Therapy"
  | "ABA"
  | "Feeding"
  | "School Readiness";

export type CaseStudy = {
  slug: string;
  /** Parent's first name until a child's name is provided. Omitted when the name is not known yet. */
  childFirstName?: string;
  ageAtStart?: string;
  service?: CaseStudyService;
  title: string;
  summary: string;
  bubbleClip: string;
  poster: string;
  fullVideo: string;
  fullVideoPoster: string;
  transcript?: string;
  captionsUrl?: string;
  parentName?: string;
  pullQuote?: string;
  startingPoint?: string;
  whatWeDid?: string;
  whereNow?: string;
  durationMonths?: number;
  consentVerified: boolean;
  publishedAt: string;
};

export const SERVICE_PAGES: Record<CaseStudyService, { href: string; label: string }> = {
  "Speech Therapy": {
    href: "/services/speech-language-therapy",
    label: "Speech & Language Therapy",
  },
  "Occupational Therapy": {
    href: "/services/occupational-therapy",
    label: "Occupational Therapy",
  },
  ABA: {
    href: "/contact-us",
    label: "Contact the clinic",
  },
  Feeding: {
    href: "/services/feeding-therapy",
    label: "Feeding Therapy",
  },
  "School Readiness": {
    href: "/services/school-readiness-program",
    label: "School Readiness Program",
  },
};

const FULL_VIDEO =
  "https://media.thespeechclinic.ae/website/videos/case-studies/testimonial-video-";

export const caseStudies: CaseStudy[] = [
  {
    slug: "parent-story-001",
    title: "A parent story",
    summary: "A parent shares their family's experience at The Speech Clinic.",
    bubbleClip: "/videos/bubble/parent-001.mp4",
    poster: "/videos/bubble/parent-001.jpg",
    fullVideo: `${FULL_VIDEO}001.mp4`,
    fullVideoPoster: "/videos/bubble/parent-001.jpg",
    consentVerified: true,
    publishedAt: "2026-08-13",
  },
  {
    slug: "vanitha-story",
    childFirstName: "Vanitha",
    parentName: "Vanitha",
    title: "A parent story",
    summary: "Vanitha shares her family's experience at The Speech Clinic.",
    bubbleClip: "/videos/bubble/vanitha.mp4",
    poster: "/videos/bubble/vanitha.jpg",
    fullVideo: `${FULL_VIDEO}002.mp4`,
    fullVideoPoster: "/videos/bubble/vanitha.jpg",
    consentVerified: true,
    publishedAt: "2026-09-22",
  },
  {
    slug: "shouvik-story",
    childFirstName: "Shouvik",
    parentName: "Shouvik",
    title: "A parent story",
    summary: "Shouvik shares his family's experience at The Speech Clinic.",
    bubbleClip: "/videos/bubble/shouvik.mp4",
    poster: "/videos/bubble/shouvik.jpg",
    fullVideo: `${FULL_VIDEO}003.mp4`,
    fullVideoPoster: "/videos/bubble/shouvik.jpg",
    consentVerified: true,
    publishedAt: "2026-09-23",
  },
  {
    slug: "iram-story",
    childFirstName: "Iram",
    parentName: "Iram",
    title: "A parent story",
    summary: "Iram shares her family's experience at The Speech Clinic.",
    bubbleClip: "/videos/bubble/iram.mp4",
    poster: "/videos/bubble/iram.jpg",
    fullVideo: `${FULL_VIDEO}004.mp4`,
    fullVideoPoster: "/videos/bubble/iram.jpg",
    consentVerified: true,
    publishedAt: "2026-09-17",
  },
  {
    slug: "parent-story-005",
    title: "A parent story",
    summary: "A parent shares their family's experience at The Speech Clinic.",
    bubbleClip: "/videos/bubble/parent-005.mp4",
    poster: "/videos/bubble/parent-005.jpg",
    fullVideo: `${FULL_VIDEO}005.mp4`,
    fullVideoPoster: "/videos/bubble/parent-005.jpg",
    consentVerified: true,
    publishedAt: "2026-08-21",
  },
  {
    slug: "sample-pending-consent",
    childFirstName: "Sample",
    ageAtStart: "2 years",
    service: "Feeding",
    title: "This story is not published",
    summary: "Held back until a parent confirms consent.",
    bubbleClip: "/videos/bubble/sample.mp4",
    poster: "/videos/bubble/sample.jpg",
    fullVideo: "/videos/stories/sample.mp4",
    fullVideoPoster: "/videos/stories/sample.jpg",
    transcript: "Unpublished sample. Do not display.",
    captionsUrl: "/videos/captions/sample.vtt",
    parentName: "Parent",
    pullQuote: "Not for publication.",
    startingPoint: "Not for publication.",
    whatWeDid: "Not for publication.",
    whereNow: "Not for publication.",
    durationMonths: 1,
    consentVerified: false,
    publishedAt: "2026-01-01",
  },
];

export function storyLabel(story: Pick<CaseStudy, "childFirstName">) {
  return story.childFirstName ? `${story.childFirstName}'s story` : "A parent story";
}

export function getPublishedCaseStudies(): CaseStudy[] {
  return caseStudies.filter((story) => story.consentVerified === true);
}

export function getCaseStudy(slug: string): CaseStudy | undefined {
  return getPublishedCaseStudies().find((story) => story.slug === slug);
}

export function isEmbedVideo(url: string): boolean {
  return /youtube\.com|youtu\.be|player\.vimeo\.com/i.test(url);
}
